import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Box, Button, Flex, Paper, Text, Transition, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ContentStoreModal from 'extensions/content-store/ContentStoreModal';
import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import staticURL from 'lib/staticURL';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useMcpDirectoryInstallHandler} from 'tenants/admin/extensions/mcp/useMcpDirectoryInstallHandler';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import getChatInputTips from './ChatInputTipsData';
import type {ChatInputTip} from './ChatInputTipsData';

const SHOWN_TIPS_STORAGE_KEY = 'benchling-chat-input-shown-tips';
const TIP_CYCLE_INTERVAL_MS = 120000;
const SLIDE_DURATION_MS = 150;
const SLIDE_OFFSET_PX = 20;

function getShownCounts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SHOWN_TIPS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as Record<string, number>;
  } catch (err) {
    if (err instanceof SyntaxError) {
      return {};
    }
    throw err;
  }
}

function recordShown(tipId: string): void {
  try {
    const counts = getShownCounts();
    counts[tipId] = (counts[tipId] ?? 0) + 1;
    localStorage.setItem(SHOWN_TIPS_STORAGE_KEY, JSON.stringify(counts));
  } catch (err) {
    if (err instanceof DOMException) {
      return;
    }
    throw err;
  }
}

/**
 * Pick the next tip via weighted random selection. Each tip's selection probability
 * is proportional to its `weight` divided by `(showCount + 1)`, so less-shown tips
 * and higher-weight tips are more likely to appear. The currently displayed tip
 * (excludeIndex) is excluded so the carousel always advances to a different tip.
 */
function pickNextTip(
  tips: ReadonlyArray<ChatInputTip>,
  counts: Record<string, number>,
  excludeIndex?: number
): number {
  let totalWeight = 0;
  const weights: Array<number> = [];
  for (let i = 0; i < tips.length; i++) {
    if (i === excludeIndex) {
      weights.push(0);
      continue;
    }
    const tipWeight = tips[i].weight ?? 1;
    const selectionWeight = tipWeight / ((counts[tips[i].id] ?? 0) + 1);
    weights.push(selectionWeight);
    totalWeight += selectionWeight;
  }
  if (totalWeight === 0) {
    return excludeIndex ?? 0;
  }
  let random = Math.random() * totalWeight;
  for (let i = 0; i < weights.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return i;
    }
  }
  return weights.length - 1;
}

/**
 * Return true if the user/tenant has every feature the tip references enabled.
 *
 * Tips that reference features the user doesn't have (e.g. Compose, Ask, Deep Research)
 * are filtered out so we don't advertise features the user cannot use.
 */
export function isTipAvailable(tip: ChatInputTip): boolean {
  if (tip.requiredAgentTypes) {
    for (const agentType of tip.requiredAgentTypes) {
      if (!isAgentTypeEnabled(agentType)) {
        return false;
      }
    }
  }
  if (tip.requiresBenchlingConfigs) {
    for (const key of tip.requiresBenchlingConfigs) {
      if (!BenchlingConfig[key]) {
        return false;
      }
    }
  }
  return true;
}

const useStyles = createStyles((theme) => ({
  icon: {
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.md,
    flexShrink: 0,
    marginTop: 2,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 700,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
  text: {
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.5,
    color: theme.colors.dark[7],
  },
  navButton: {
    color: theme.colors.blue[6],
    '&:hover': {
      backgroundColor: theme.colors.blue[1],
    },
  },
  promoIcon: {
    color: theme.colors.blue[6],
    fontSize: theme.fontSizes.xl,
    flexShrink: 0,
  },
  promoText: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    lineHeight: 1.4,
    color: theme.colors.dark[7],
  },
  promoPreviewIcons: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  promoPreviewIcon: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: `2px solid ${theme.white}`,
    objectFit: 'cover',
    backgroundColor: theme.colors.gray[1],
    '&:not(:first-of-type)': {
      marginLeft: -12,
    },
  },
  promoBrowseButton: {
    flexShrink: 0,
  },
  flexContent: {
    flex: 1,
    minWidth: 0,
  },
  navButtonGroup: {
    flexShrink: 0,
  },
}));

function PromotionalTipContent({tip, onAction}: {tip: ChatInputTip; onAction: () => void}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Flex align="center" gap="sm" className={classes.flexContent}>
      <BenchlingIcon icon={tip.icon} className={classes.promoIcon} />
      <Text className={`${classes.promoText} ${classes.flexContent}`}>{tip.text}</Text>
      {tip.previewIconPaths && tip.previewIconPaths.length > 0 && (
        <Box className={classes.promoPreviewIcons}>
          {tip.previewIconPaths.map((path) => (
            <img key={path} src={staticURL(path)} alt="" className={classes.promoPreviewIcon} />
          ))}
        </Box>
      )}
      {tip.buttonLabel && (
        <Button
          variant="ghost"
          size="xs"
          className={classes.promoBrowseButton}
          onClick={onAction}
          {...getTestProps({component: 'ChatInputTips', element: 'promoActionButton'})}
        >
          {tip.buttonLabel}
        </Button>
      )}
    </Flex>
  );
}

type SlideDirection = 'next' | 'previous';
type SlidePhase = 'idle' | 'out' | 'entering' | 'in';

function ChatInputTipsInner({tips}: {tips: ReadonlyArray<ChatInputTip>}): JSX.Element {
  const onMcpDirectoryInstall = useMcpDirectoryInstallHandler();
  const {classes} = useStyles();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();
  const [currentIndex, setCurrentIndex] = useState(() => {
    const counts = getShownCounts();
    const index = pickNextTip(tips, counts);
    recordShown(tips[index].id);
    return index;
  });
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;
  const [mounted, setMounted] = useState(true);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('next');
  const [slidePhase, setSlidePhase] = useState<SlidePhase>('idle');

  const navigateToTip = useCallback(
    (nextIndex: number, direction: SlideDirection) => {
      setSlideDirection(direction);
      setSlidePhase('out');
      timeoutRef.current = setTimeout(() => {
        recordShown(tips[nextIndex].id);
        setCurrentIndex(nextIndex);
        setSlidePhase('entering');
      }, SLIDE_DURATION_MS);
    },
    [tips]
  );

  useEffect(() => {
    if (slidePhase !== 'entering') {
      return;
    }
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = requestAnimationFrame(() => {
        setSlidePhase('in');
        timeoutRef.current = setTimeout(() => {
          setSlidePhase('idle');
        }, SLIDE_DURATION_MS);
      });
    });
  }, [slidePhase]);

  const advanceTip = useCallback(() => {
    if (tips.length <= 1) {
      return;
    }
    setMounted(false);
    timeoutRef.current = setTimeout(() => {
      const counts = getShownCounts();
      const nextIndex = pickNextTip(tips, counts, currentIndexRef.current);
      recordShown(tips[nextIndex].id);
      setCurrentIndex(nextIndex);
      setMounted(true);
    }, 200);
  }, [tips]);

  const handlePrevious = useCallback(() => {
    if (slidePhase !== 'idle') {
      return;
    }
    const prevIndex = (currentIndex - 1 + tips.length) % tips.length;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CHAT_INPUT_TIP_NAVIGATION, {
      direction: 'previous',
      fromTipId: tips[currentIndex].id,
      toTipId: tips[prevIndex].id,
    });
    navigateToTip(prevIndex, 'previous');
  }, [currentIndex, slidePhase, navigateToTip, tips]);

  const handleNext = useCallback(() => {
    if (slidePhase !== 'idle') {
      return;
    }
    const nextIndex = (currentIndex + 1) % tips.length;
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CHAT_INPUT_TIP_NAVIGATION, {
      direction: 'next',
      fromTipId: tips[currentIndex].id,
      toTipId: tips[nextIndex].id,
    });
    navigateToTip(nextIndex, 'next');
  }, [currentIndex, slidePhase, navigateToTip, tips]);

  useEffect(() => {
    const interval = setInterval(advanceTip, TIP_CYCLE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeoutRef.current);
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [advanceTip]);

  const tip = tips[currentIndex];
  const isPromotional = tip.tipType === 'promotional';

  const handlePromoAction = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_CHAT_INPUT_TIP_NAVIGATION, {
      direction: 'action',
      fromTipId: tip.id,
      toTipId: tip.id,
    });
    if (tip.id === 'promo-mcp-connectors') {
      legacyRunModal(ContentStoreModal, {
        extensionType: ExtensionType.MCP_SERVER,
        onInstall: onMcpDirectoryInstall,
      });
    }
  }, [tip.id, onMcpDirectoryInstall]);

  const sign = slideDirection === 'next' ? 1 : -1;
  let contentStyle: React.CSSProperties;
  if (slidePhase === 'out') {
    contentStyle = {
      transform: `translateX(${-sign * SLIDE_OFFSET_PX}px)`,
      opacity: 0,
      transition: `transform ${SLIDE_DURATION_MS}ms ease-in, opacity ${SLIDE_DURATION_MS}ms ease-in`,
    };
  } else if (slidePhase === 'entering') {
    contentStyle = {
      transform: `translateX(${sign * SLIDE_OFFSET_PX}px)`,
      opacity: 0,
    };
  } else if (slidePhase === 'in') {
    contentStyle = {
      transform: 'translateX(0)',
      opacity: 1,
      transition: `transform ${SLIDE_DURATION_MS}ms ease-out, opacity ${SLIDE_DURATION_MS}ms ease-out`,
    };
  } else {
    contentStyle = {transform: 'translateX(0)', opacity: 1};
  }

  return (
    <Transition mounted={mounted} transition="fade" duration={200}>
      {(transitionStyles) => (
        <Paper
          withBorder={true}
          radius="lg"
          p="md"
          mt="md"
          style={transitionStyles}
          sx={(theme) => ({
            borderColor: theme.colors.blue[1],
            backgroundColor: theme.colors.blue[0],
            overflow: 'hidden',
          })}
          {...getTestProps({component: 'ChatInputTips', element: 'root'})}
        >
          <Flex align={isPromotional ? 'center' : 'flex-start'} gap="sm" style={contentStyle}>
            {isPromotional ? (
              <PromotionalTipContent tip={tip} onAction={handlePromoAction} />
            ) : (
              <Flex align="flex-start" gap="sm" className={classes.flexContent}>
                <BenchlingIcon icon={tip.icon} className={classes.icon} />
                <Box className={classes.flexContent}>
                  <Text className={classes.title}>{tip.title}</Text>
                  <Text className={classes.text}>{tip.text}</Text>
                </Box>
              </Flex>
            )}
            <Flex align="center" gap={4} className={classes.navButtonGroup}>
              <ActionIcon
                size="sm"
                variant="subtle"
                className={classes.navButton}
                onClick={handlePrevious}
                aria-label="Previous tip"
                {...getTestProps({component: 'ChatInputTips', element: 'previousButton'})}
              >
                <BenchlingIcon icon="chevron-left" />
              </ActionIcon>
              <ActionIcon
                size="sm"
                variant="subtle"
                className={classes.navButton}
                onClick={handleNext}
                aria-label="Next tip"
                {...getTestProps({component: 'ChatInputTips', element: 'nextButton'})}
              >
                <BenchlingIcon icon="chevron-right" />
              </ActionIcon>
            </Flex>
          </Flex>
        </Paper>
      )}
    </Transition>
  );
}

export default function ChatInputTips(): JSX.Element | null {
  const {llmChatTips} = useRolloutFlags();
  const availableTips = useMemo(() => getChatInputTips().filter((tip) => isTipAvailable(tip)), []);
  if (llmChatTips !== true) {
    return null;
  }
  if (availableTips.length === 0) {
    return null;
  }
  // Key the inner component by the set of available tip ids. If feature gating changes at runtime
  // (e.g. LD flag updates), this remounts the carousel with fresh state so `currentIndex` can't
  // point past the end of the new (shorter) tips list.
  const tipsKey = availableTips.map((tip) => tip.id).join(',');
  return <ChatInputTipsInner key={tipsKey} tips={availableTips} />;
}
